import { ArrowLeft, Pencil, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useEmoviDocTitle } from './hooks/useEmoviDocTitle';
import type { EmoviDocConversation } from './types';

type EmoviDocChatHeaderProps = {
  patientId: number;
  conversationId: number | null;
  displayTitle: string;
  onConversationUpdated: (conv: EmoviDocConversation) => void;
  onBack: () => void;
  onDeleteRequest: (id: number) => void;
  isDeletingConversation: number | null;
};

export function EmoviDocChatHeader({
  patientId,
  conversationId,
  displayTitle,
  onConversationUpdated,
  onBack,
  onDeleteRequest,
  isDeletingConversation,
}: EmoviDocChatHeaderProps) {
  const { titleDraft, setTitleDraft, isEditing, setIsEditing, isSaving, save } = useEmoviDocTitle({
    patientId,
    conversationId,
    initialTitle: displayTitle,
  });

  const handleSaveTitle = async () => {
    try {
      const updated = await save();
      if (updated) {
        onConversationUpdated(updated);
      }
    } catch {
      // Error already logged inside hook
    }
  };

  return (
    <div className="h-14 flex-shrink-0 border-b border-border px-3 flex items-center justify-between gap-2">
      <div className="flex items-center gap-2 min-w-0">
        <Button type="button" variant="ghost" size="icon" onClick={onBack} className="h-8 w-8" aria-label="Torna alla lista">
          <ArrowLeft className="h-4 w-4" />
        </Button>

        {!isEditing ? (
          <div className="flex items-center gap-1 min-w-0">
            <p className="text-sm font-medium truncate">{displayTitle}</p>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 text-muted-foreground"
              aria-label="Modifica titolo conversazione"
              onClick={() => setIsEditing(true)}
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          </div>
        ) : (
          <form
            className="flex flex-1 min-w-0 items-center gap-2"
            onSubmit={async (event) => {
              event.preventDefault();
              await handleSaveTitle();
              setIsEditing(false);
            }}
          >
            <Input
              value={titleDraft}
              maxLength={255}
              autoFocus
              onChange={(event) => setTitleDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setIsEditing(false);
                }
              }}
              className="h-8 text-xs"
            />
            <Button
              type="submit"
              size="sm"
              variant="outline"
              isLoading={isSaving}
              onMouseDown={(e) => e.preventDefault()}
            >
              Salva
            </Button>
          </form>
        )}
      </div>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        aria-label="Elimina conversazione"
        className="h-8 w-8 text-muted-foreground hover:text-destructive"
        isLoading={conversationId ? isDeletingConversation === conversationId : false}
        onClick={() => {
          if (!conversationId) return;
          onDeleteRequest(conversationId);
        }}
      >
        <Trash2 className="h-4 w-4" />
      </Button>
    </div>
  );
}
